"use client";

import type { SearchResult as SearchResultType } from "@/lib/searchRegistry";
import SearchResultItem from "./SearchResultItem";
import { Search as SearchIcon, SearchX } from "lucide-react";
import React from "react";

interface SearchResultsProps {
  results: SearchResultType[];
  searchQuery: string;
  activeIndex: number;
  onSelect: (item: SearchResultType) => void;
  isLoading: boolean;
}

export default function SearchResults({
  results,
  searchQuery,
  activeIndex,
  onSelect,
  isLoading,
}: SearchResultsProps) {
  if (isLoading) {
    return (
      <div className="search-results-panel empty-state">
        <div className="search-spinner-container">
          <div className="search-spinner"></div>
          <span>Building search index...</span>
        </div>
      </div>
    );
  }

  if (searchQuery.trim().length < 2) {
    return (
      <div className="search-results-panel empty-state">
        <div className="search-empty-state">
          <SearchIcon className="search-empty-icon" size={48} strokeWidth={1.5} />
          <p className="search-empty-title">Start searching</p>
          <p className="search-hint-text">Type at least 2 characters to find calculators, guides, comparisons, and glossary terms.</p>
        </div>
      </div>
    );
  }

  // Group and limit results (max 10 per section, max 40 total)
  const calculators = results.filter((r) => r.type === "calculator").slice(0, 10);
  const blogs = results.filter((r) => r.type === "blog").slice(0, 10);
  const comparisons = results.filter((r) => r.type === "comparison").slice(0, 10);
  const glossaries = results.filter((r) => r.type === "glossary").slice(0, 10);

  const totalCount = calculators.length + blogs.length + comparisons.length + glossaries.length;

  if (totalCount === 0) {
    return (
      <div className="search-results-panel empty-state">
        <div className="search-empty-state">
          <SearchX className="search-empty-icon" size={48} strokeWidth={1.5} />
          <p className="search-empty-title">No results found</p>
          <p className="search-no-results">We couldn&apos;t find anything matching &ldquo;{searchQuery}&rdquo;. Try different keywords or check your spelling.</p>
        </div>
      </div>
    );
  }

  // Helper to determine the maximum search score in a section
  const getSectionMaxScore = (items: SearchResultType[]) => {
    if (items.length === 0) return -1;
    return Math.max(...items.map((item) => item.score || 0));
  };

  // Create a flattened list of items for index matching
  const flatItems: SearchResultType[] = [];
  
  const sections = [
    { title: "CALCULATORS", items: calculators },
    { title: "BLOG POSTS", items: blogs },
    { title: "COMPARISONS", items: comparisons },
    { title: "GLOSSARY TERMS", items: glossaries },
  ];

  // Sort sections by their highest score in descending order
  // This ensures that the section with the absolute best match is displayed at the very top.
  sections.sort((a, b) => getSectionMaxScore(b.items) - getSectionMaxScore(a.items));

  return (
    <div className="search-results-panel" role="listbox" id="search-results-listbox">
      {sections.map((section) => {
        if (section.items.length === 0) return null;

        return (
          <div key={section.title} className="search-results-section">
            <div className="search-results-section-header">{section.title}</div>
            <div className="search-results-section-content">
              {section.items.map((item) => {
                flatItems.push(item);
                const currentFlatIndex = flatItems.length - 1;
                const isActive = currentFlatIndex === activeIndex;

                return (
                  <SearchResultItem
                    key={item.id}
                    item={item}
                    isActive={isActive}
                    onClick={() => onSelect(item)}
                    searchQuery={searchQuery}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
